// src/pages/tender/LoaManager.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";

const formatINR = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function LoaManager() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);

  const [user, setUser] = useState(null);
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [signedFile, setSignedFile] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (!u) {
        toast.error("Please login first.");
        navigate("/tender/login", { replace: true });
        return;
      }
      setUser(u);
    });

    return () => unsub();
  }, [auth, navigate]);

  const loadTender = async () => {
    if (!tenderId || !user) return;

    try {
      setLoading(true);

      const snap = await getDoc(doc(db, "tenders", tenderId));

      if (!snap.exists()) {
        toast.error("Project not found.");
        navigate("/tender/contractor?section=projects");
        return;
      }

      const data = { id: snap.id, ...snap.data() };

      if (data.awardedVendorUid && data.awardedVendorUid !== user.uid) {
        toast.error("This project is not assigned to your login.");
        navigate("/tender/contractor?section=projects");
        return;
      }

      setTender(data);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load LOA details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderId, user]);

  const handleUploadSignedLoa = async () => {
    if (!signedFile) {
      toast.error("Please select signed LOA PDF/image.");
      return;
    }

    try {
      setUploading(true);

      const safeName = signedFile.name.replace(/\s+/g, "_");
      const path = `tender_loa_signed/${tenderId}/${user.uid}/${Date.now()}_${safeName}`;
      const sRef = ref(storage, path);

      await uploadBytes(sRef, signedFile);
      const url = await getDownloadURL(sRef);

      await updateDoc(doc(db, "tenders", tenderId), {
        signedLoaUrl: url,
        signedLoaPath: path,
        signedLoaName: signedFile.name,
        loaStatus: "SIGNED_SUBMITTED",
        loaSubmittedAt: serverTimestamp(),
        loaSubmittedBy: user.uid,
      });

      toast.success("Signed LOA uploaded successfully.");
      setSignedFile(null);
      await loadTender();
    } catch (e) {
      console.error(e);
      toast.error("Signed LOA upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (loading || !tender) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="rounded-2xl bg-white px-8 py-5 shadow text-sm text-slate-600">
            Loading LOA details...
          </div>
        </div>
      </>
    );
  }

  const projectName = tender.title || tender.projectName || "Project";
  const pacsName = tender.ownerCompany || tender.pacsName || tender.organization || "—";
  const district = tender.city || tender.district || "—";
  const awardValue = tender.awardedAmount || tender.awardedValue || tender.amount || 0;

  const loaPdfUrl = tender.loaPdfUrl || tender.workOrderPdfUrl || "";
  const signedLoaUrl = tender.signedLoaUrl || "";
  const loaStatus = tender.loaStatus || (signedLoaUrl ? "SIGNED_SUBMITTED" : "PENDING");

  return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 px-4 py-8">
        <div className="mx-auto max-w-5xl">
          <button
            type="button"
            onClick={() => navigate("/tender/contractor?section=projects")}
            className="mb-4 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-700 shadow-sm hover:bg-slate-50"
          >
            ← Back to Awarded Projects
          </button>

          <div className="overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-xl">
            <div className="bg-slate-950 px-6 py-6 text-white">
              <div className="text-[11px] font-black uppercase tracking-[0.25em] text-emerald-300">
                Letter of Award / Work Order
              </div>
              <h1 className="mt-2 text-2xl font-black">{projectName}</h1>
              <div className="mt-2 text-xs text-slate-300">
                Tender ID: {tenderId}
              </div>
            </div>

            <div className="grid gap-4 p-6 md:grid-cols-4">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[10px] font-black uppercase text-slate-400">
                  PACS / Society
                </div>
                <div className="mt-1 text-sm font-black text-slate-900">
                  {pacsName}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[10px] font-black uppercase text-slate-400">
                  District
                </div>
                <div className="mt-1 text-sm font-black text-slate-900">
                  {district}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[10px] font-black uppercase text-slate-400">
                  Award Value
                </div>
                <div className="mt-1 text-sm font-black text-emerald-800">
                  {formatINR(awardValue)}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-[10px] font-black uppercase text-slate-400">
                  LOA Status
                </div>
                <div className="mt-1 text-sm font-black text-amber-700">
                  {loaStatus}
                </div>
              </div>
            </div>

            <div className="grid gap-5 px-6 pb-6 md:grid-cols-2">
              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-black text-emerald-800">
                  Download LOA
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Download the original LOA / Work Order, print it, sign and stamp it.
                </p>

                {loaPdfUrl ? (
                  <a
                    href={loaPdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex rounded-full bg-emerald-600 px-5 py-2 text-xs font-black text-white hover:bg-emerald-700"
                  >
                    Download LOA PDF
                  </a>
                ) : (
                  <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-xs font-bold text-amber-700">
                    LOA PDF has not been uploaded by PMC yet.
                  </div>
                )}
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-5">
                <h2 className="text-sm font-black text-emerald-800">
                  Upload Signed LOA
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  Upload signed/stamped LOA PDF or image. It will be visible in PMC dashboard.
                </p>

                <input
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={(e) => setSignedFile(e.target.files?.[0] || null)}
                  className="mt-4 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
                />

                <button
                  type="button"
                  disabled={uploading}
                  onClick={handleUploadSignedLoa}
                  className="mt-4 rounded-full bg-slate-950 px-5 py-2 text-xs font-black text-white hover:bg-slate-800 disabled:bg-slate-300"
                >
                  {uploading ? "Uploading..." : "Submit Signed LOA"}
                </button>

                {signedLoaUrl && (
                  <div className="mt-4 rounded-2xl bg-emerald-50 p-4 text-xs">
                    <div className="font-black text-emerald-800">
                      Signed LOA submitted.
                    </div>
                    <a
                      href={signedLoaUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block font-black text-emerald-700 underline"
                    >
                      View Submitted File
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}